import { useEffect, useState } from "react";
import { Seo } from "../components/Seo";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import api from "../lib/api";
import { PartCard } from "../components/parts/PartCard";

export default function ProductDetailPage() {
  const { sku } = useParams();
  const [part, setPart] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [relatedParts, setRelatedParts] = useState([]);

  useEffect(() => {
    setNotFound(false);
    setPart(null);
    setRelatedParts([]);
    api
      .get(`/parts/${sku}`)
      .then((res) => {
        setPart(res.data);
        setActiveImage(res.data.image);
        api
          .get("/parts", { params: { category: res.data.category } })
          .then((r) => setRelatedParts(r.data.filter((p) => p.sku !== res.data.sku).slice(0, 4)))
          .catch(() => setRelatedParts([]));
      })
      .catch(() => setNotFound(true));
  }, [sku]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-obsidian pt-40 text-center" data-testid="product-not-found">
        <p className="text-zinc-400 font-mono">Part not found.</p>
        <Link to="/catalog" className="text-amber-light underline mt-4 inline-block">Back to Catalog</Link>
      </div>
    );
  }

  if (!part) return <div className="min-h-screen bg-obsidian pt-40 text-center text-zinc-500 font-mono">Loading...</div>;

  const inStock = part.stock_status === "in_stock";
  const gallery = [part.image, ...(part.images || [])].filter(Boolean);

  return (
    <div className="min-h-screen bg-obsidian pt-32 pb-24" data-testid="product-detail-page">
      <Seo
        title={`${part.name} | Orion Services LLC`}
        description={part.description}
        image={part.image}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/catalog" className="inline-flex items-center gap-2 text-xs font-mono uppercase text-zinc-500 hover:text-amber-light mb-8" data-testid="product-back-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Catalog
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="relative aspect-square rounded-md overflow-hidden border border-amber-500/10">
              <img src={activeImage} alt={part.name} className="w-full h-full object-cover" data-testid="product-detail-main-image" />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-4" data-testid="product-detail-gallery">
                {gallery.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    data-testid={`product-detail-thumb-${idx}`}
                    className={`h-16 w-16 rounded-md overflow-hidden border-2 transition-colors ${activeImage === img ? "border-amber-light" : "border-amber-500/10 hover:border-amber-500/40"}`}
                  >
                    <img src={img} alt={`${part.name} angle ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <span data-testid="product-detail-sku" className="text-xs font-mono font-semibold tracking-wider text-amber-400/90 bg-amber-950/40 border border-amber-500/30 px-2 py-0.5 rounded">
              {part.sku}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl uppercase text-white mt-4 leading-tight">{part.name}</h1>
            <p className="text-xs font-mono uppercase text-zinc-500 mt-2">{part.category}</p>

            <div className="flex items-center gap-2 mt-4" data-testid="product-detail-stock-status">
              {inStock ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-rose-400" />}
              <span className={inStock ? "text-emerald-300 text-sm" : "text-rose-300 text-sm"}>
                {inStock ? "In Stock — Ships within 2-3 business days" : "Out of Stock — Request lead-time estimate"}
              </span>
            </div>

            <div className="font-display text-4xl text-amber-light mt-6">${part.price.toLocaleString()}</div>

            <p className="text-zinc-400 leading-relaxed mt-6">{part.description}</p>

            <div className="mt-6 space-y-2 border-t border-amber-500/10 pt-6">
              <p className="text-sm text-zinc-300"><span className="text-zinc-500">Compatibility:</span> {part.compatibility}</p>
              {Object.entries(part.specs || {}).map(([k, v]) => (
                <p key={k} className="text-sm text-zinc-300 capitalize"><span className="text-zinc-500">{k.replace(/_/g, " ")}:</span> {v}</p>
              ))}
            </div>

            <Link
              to={`/quote?sku=${part.sku}`}
              data-testid="product-detail-quote-btn"
              className="inline-flex items-center gap-2 bg-amber-light text-obsidian font-semibold px-7 py-3.5 rounded-sm uppercase text-sm tracking-wide hover:bg-white transition-colors duration-300 mt-8"
            >
              Request Quote for This Part
            </Link>
          </motion.div>
        </div>

        <div className="mt-16 pt-10 border-t border-amber-500/10 max-w-3xl" data-testid="product-detail-buying-guide">
          <h2 className="font-display text-2xl uppercase text-white mb-4">Why Source {part.category} From Orion Services</h2>
          <p className="text-zinc-400 leading-relaxed mb-4">
            {part.sku} is verified against our internal fitment and stress-test protocol before it's cleared into the
            {" "}{part.category.toLowerCase()} index, so what you see is what actually ships — not a generic listing pulled
            from a third-party feed. Every unit is inspected for tolerance, finish, and dimensional accuracy against the
            compatibility record listed above, whether it's headed to a long-haul fleet depot or a working farm.
          </p>
          <p className="text-zinc-400 leading-relaxed mb-4">
            {inStock
              ? "Because this SKU is currently in stock at our Saint Johns, Florida facility, orders placed through the quote form typically ship within 2-3 business days with tracking sent directly to your email."
              : "This SKU is temporarily out of stock, but requesting a quote gets you a real lead-time estimate from our team within 24 hours — not an indefinite backorder with no visibility."}
            {" "}Our technicians — Curt, Eric, and Abhi — personally review every quote request to confirm the part
            matches your equipment before dispatch, cutting down on costly return shipments and downtime.
          </p>
          <p className="text-zinc-400 leading-relaxed">
            Can't find the exact fitment you need, or need a modified version of this part? Our{" "}
            <Link to="/custom-orders" className="text-amber-light underline">custom fabrication team</Link> can
            reverse-engineer this component to match a discontinued model, a non-standard bolt pattern, or a
            one-off fleet modification, with CNC machining and pressure testing before it ships.
          </p>
        </div>

        {relatedParts.length > 0 && (
          <div className="mt-16 pt-10 border-t border-amber-500/10" data-testid="product-detail-related-parts">
            <h2 className="font-display text-2xl uppercase text-white mb-6">More {part.category}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedParts.map((p) => (
                <PartCard key={p.sku} part={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
